import { html } from "lit-html";
import i18next from "i18next";
import dayjs from "dayjs";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import { useEffectWithCleanup, useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { COMMON_DAYJS_FORMAT } from "Services/constants";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { isTabletOrMobileResolution, capitalizeAndRemoveDot } from "Services/common";
import classNames from "classnames";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { useTimetable } from "./useTimetable";
import { useMobileSearchboxDateSelectorModal } from "./useMobileSearchboxDateSelectorModal";
import { useNewFlightDatePicker } from "./useNewFlightDatePicker";
dayjs.extend(CustomParseFormat);

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
	dynamicSettings: DynamicSettings;
	openPassengerSelector: () => void;
	startLoading: () => LoaderInstance;
	stopLoading: (loader: LoaderInstance) => void;
}

export const useNewFlightDateSelector = (props: Props) => {
	const [hasAutoOpenedOnce, setHasAutoOpenedOnce] = useState(false);

	const timetable = useTimetable({
		startLoading: props.startLoading,
		stopLoading: props.stopLoading,
		currentSearchManager: props.currentSearchManager,
		dynamicSettings: props.dynamicSettings,
	});

	const timetableReady =
		props.currentSearchManager.currentSearch.originCity &&
		props.currentSearchManager.currentSearch.destinationCity &&
		!timetable.isLoading;

	const mobileDateSelectorModal = useMobileSearchboxDateSelectorModal({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		currentSearchManager: props.currentSearchManager,
		timetable,
		onDepartureChange: (date) => {
			props.currentSearchManager.setReturnDate(undefined);
			props.currentSearchManager.setDepartureDate(date);
		},
		onReturnChange: (date) => {
			props.currentSearchManager.setReturnDate(date);

			// DEVNOTE: Add a small delay to ensure the date modal closes first
			window.setTimeout(() => {
				props.openPassengerSelector();
			}, 350);
		},
	});

	const desktopDepartureDate = useNewFlightDatePicker({
		availableSchedules: timetable.outboundAvailableDates,
		culture: props.culture,
		disabledDates: timetable.disabledDepartureDates,
		dynamicSettings: props.dynamicSettings,
		inputTestId: "",
		isDisabled: !timetableReady,
		isLoading: timetable.isLoading,
		isReturnDate: false,
		max: timetable.maxDepartureDate,
		min: timetable.minDepartureDate,
		placeHolder: i18next.t("Fecha Ida"),
		rangeStart: undefined,
		value: props.currentSearchManager.currentSearch.departureDate,
		isUsingAmericanMiles: props.currentSearchManager.currentSearch.useAmericanMiles,
		onChange: (date) => {
			props.currentSearchManager.setReturnDate(undefined);
			props.currentSearchManager.setDepartureDate(date);

			if (props.currentSearchManager.currentSearch.isOneWay) return;
			// DEVNOTE: Add a small delay to ensure the disabled dates are correctly calculated
			window.setTimeout(() => {
				desktopReturnDate.open();
			}, 100);
		},
	});

	const desktopReturnDate = useNewFlightDatePicker({
		availableSchedules: timetable.inboundAvailableDates,
		culture: props.culture,
		disabledDates: timetable.disabledReturnDates,
		dynamicSettings: props.dynamicSettings,
		inputTestId: "",
		isDisabled:
			!timetableReady ||
			!props.currentSearchManager.currentSearch.departureDate ||
			props.currentSearchManager.currentSearch.isOneWay,
		isLoading: timetable.isLoading,
		isReturnDate: true,
		max: timetable.maxReturnDate,
		min: timetable.minReturnDate,
		placeHolder: i18next.t("Fecha Vuelta"),
		rangeStart: props.currentSearchManager.currentSearch.departureDate,
		value: props.currentSearchManager.currentSearch.returnDate,
		isUsingAmericanMiles: props.currentSearchManager.currentSearch.useAmericanMiles,
		onChange: (date) => {
			props.currentSearchManager.setReturnDate(date);

			// DEVNOTE: Add a small delay to ensure the date picker closes first
			window.setTimeout(() => {
				props.openPassengerSelector();
			}, 100);
		},
	});

	const handleMobileDepartureInputClick = () => {
		if (!timetableReady) return;
		PUB_SUBS.MobileSearchboxModalOpened.publish({});
		mobileDateSelectorModal.openAsDeparture();
	};

	const handleMobileReturnInputClick = () => {
		if (!props.currentSearchManager.currentSearch.departureDate || !timetableReady) return;
		PUB_SUBS.MobileSearchboxModalOpened.publish({});
		mobileDateSelectorModal.openAsReturn();
	};

	const autoOpenDeparture = () => {
		if (!timetableReady || props.currentSearchManager.currentSearch.departureDate || hasAutoOpenedOnce) return;

		setHasAutoOpenedOnce(true);
		if (isTabletOrMobileResolution()) {
			PUB_SUBS.MobileSearchboxModalOpened.publish({});
			mobileDateSelectorModal.openAsDeparture();
		} else {
			desktopDepartureDate.open();
		}
	};

	useEffectWithoutCleanup(() => {
		setHasAutoOpenedOnce(false);
	}, [
		props.currentSearchManager.currentSearch.originCity?.code,
		props.currentSearchManager.currentSearch.destinationCity?.code,
	]);

	useEffectWithCleanup(() => {
		// DEVNOTE: This delay is necessary to wait until the timetable starts loading
		const timeout = window.setTimeout(() => {
			autoOpenDeparture();
		}, 300);

		return () => window.clearTimeout(timeout);
	}, [
		timetable.isLoading,
		props.currentSearchManager.currentSearch.departureDate?.format(COMMON_DAYJS_FORMAT),
		props.currentSearchManager.currentSearch.originCity?.code,
		props.currentSearchManager.currentSearch.destinationCity?.code,
	]);

	const mobileInputsTemplate = () => html`
		<div @click=${handleMobileDepartureInputClick}>
			<div
				class=${classNames(
					"relative flex h-[34px] w-full cursor-pointer items-center rounded-lg border border-solid bg-white px-4",
					{
						"border-[#c1c1c1] text-[#c1c1c1]": !props.currentSearchManager.currentSearch.departureDate,
						"border-n-blue text-n-blue": props.currentSearchManager.currentSearch.departureDate,
						"pointer-events-none opacity-75": !timetableReady,
					}
				)}
			>
				<input
					readonly
					class="w-full cursor-pointer appearance-none border-none bg-transparent px-0 pb-0 pt-0.5 text-base outline-hidden placeholder:text-base/none placeholder:text-n-gray"
					placeholder=${i18next.t("Fecha Ida")}
					value=${props.currentSearchManager.currentSearch.departureDate
						? capitalizeAndRemoveDot(
								props.currentSearchManager.currentSearch.departureDate.format("ddd DD-MM")
							)
						: ""}
				/>
				<i class="jsh-calendar absolute right-4 top-1/2 -translate-y-1/2 transform"></i>
			</div>
		</div>
		${!props.currentSearchManager.currentSearch.isOneWay
			? html`
					<div @click=${handleMobileReturnInputClick}>
						<div
							class=${classNames(
								"relative flex h-[34px] w-full cursor-pointer items-center rounded-lg border border-solid bg-white px-4",
								{
									"border-[#c1c1c1] text-[#c1c1c1]":
										!props.currentSearchManager.currentSearch.returnDate,
									"border-n-blue text-n-blue": props.currentSearchManager.currentSearch.returnDate,
									"pointer-events-none opacity-75":
										!timetableReady || !props.currentSearchManager.currentSearch.departureDate,
								}
							)}
						>
							<input
								readonly
								class="w-full cursor-pointer appearance-none border-none bg-transparent px-0 pb-0 pt-0.5 text-base outline-hidden placeholder:text-base/none placeholder:text-n-gray"
								placeholder=${i18next.t("Fecha Vuelta")}
								value=${props.currentSearchManager.currentSearch.returnDate
									? capitalizeAndRemoveDot(
											props.currentSearchManager.currentSearch.returnDate.format("ddd DD-MM")
										)
									: ""}
							/>
							<i class="jsh-calendar absolute right-4 top-1/2 -translate-y-1/2 transform"></i>
						</div>
					</div>
				`
			: ""}
		${mobileDateSelectorModal.htmlTemplate()}
	`;

	const htmlTemplate = () =>
		isTabletOrMobileResolution()
			? mobileInputsTemplate()
			: html`
					<div>${desktopDepartureDate.htmlTemplate()}</div>
					<div>${desktopReturnDate.htmlTemplate()}</div>
				`;

	return {
		selectedPriceOutbound: timetable.selectedPriceOutbound,
		selectedPriceInbound: timetable.selectedPriceInbound,
		htmlTemplate,
		setMinReturnDate: timetable.setMinReturnDate,
		openModal: () =>
			isTabletOrMobileResolution() ? mobileDateSelectorModal.openAsDeparture() : desktopDepartureDate.open(),
	};
};
